import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaGraduationCap, FaBriefcase } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import Loader from '../components/shared/Loader';
import './AuthPage.css';

// ─── Premium Dashboard Choice Modal ────────────────────────────────────────────
const DashboardChoiceModal = ({ userName, onChooseLearning, onChooseJobPortal }) => (
    <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(240, 245, 255, 0.92)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
    }}>
        <style>{`
            @keyframes choiceFadeIn {
                from { opacity: 0; transform: translateY(20px) scale(0.97); }
                to   { opacity: 1; transform: translateY(0)    scale(1);    }
            }
            @keyframes floatEmoji {
                0%, 100% { transform: translateY(0px);  }
                50%       { transform: translateY(-10px); }
            }
            .choice-card-btn {
                transition: all 0.35s cubic-bezier(0.23, 1, 0.32, 1);
                cursor: pointer;
                background: none;
                outline: none;
            }
            .choice-card-btn:hover {
                transform: translateY(-8px) scale(1.03);
            }
            .choice-card-btn:hover .choice-cta-pill {
                filter: brightness(1.15);
                box-shadow: 0 8px 28px var(--pill-shadow) !important;
            }
        `}</style>

        <div style={{
            background: 'linear-gradient(145deg, #08172c 0%, #0d2540 55%, #0a1e38 100%)',
            borderRadius: '32px',
            border: '1px solid rgba(212,175,55,0.35)',
            padding: '56px 52px 44px',
            maxWidth: '740px',
            width: '100%',
            boxShadow: '0 50px 140px rgba(0,0,0,0.65), 0 0 0 1px rgba(212,175,55,0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
            textAlign: 'center',
            animation: 'choiceFadeIn 0.55s cubic-bezier(0.16,1,0.3,1) both',
            position: 'relative',
            overflow: 'hidden',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
        }}>
            {/* Decorative top glow */}
            <div style={{
                position: 'absolute',
                top: '-60px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '320px',
                height: '160px',
                background: 'radial-gradient(ellipse, rgba(212,175,55,0.18) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />

            {/* Welcome pill */}
            <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.4)',
                borderRadius: '100px',
                padding: '7px 20px',
                fontSize: '13px',
                fontWeight: 700,
                color: '#b8860b',
                letterSpacing: '0.6px',
                marginBottom: '28px',
                textTransform: 'uppercase',
            }}>
                ✦ &nbsp;Welcome back, {userName}!
            </div>

            {/* Headline */}
            <h2 style={{
                fontSize: '30px',
                fontWeight: 900,
                color: '#0f172a',
                margin: '0 0 12px',
                lineHeight: 1.2,
                letterSpacing: '-0.5px',
            }}>
                Where are you headed today?
            </h2>
            <p style={{
                color: '#64748b',
                fontSize: '15px',
                margin: '0 0 40px',
                lineHeight: 1.65,
            }}>
                You have access to both platforms. Pick your destination below.
            </p>

            {/* The two choice cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '22px',
            }}>

                {/* ── Learning / Student Card ── */}
                <button
                    className="choice-card-btn"
                    onClick={onChooseLearning}
                    style={{
                        background: '#f0f6ff',
                        border: '1.5px solid #bfdbfe',
                        borderLeft: '5px solid #3b82f6',
                        borderRadius: '18px',
                        padding: '32px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '18px',
                        textAlign: 'center',
                    }}
                >
                    {/* Icon circle */}
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '26px',
                        boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                        color: '#fff',
                        flexShrink: 0,
                    }}><FaGraduationCap /></div>

                    <div style={{ flex: 1, width: '100%' }}>
                        <h3 style={{
                            color: '#1e3a8a',
                            fontSize: '17px',
                            fontWeight: 800,
                            margin: '0 0 7px',
                        }}>Continue Learning</h3>
                        <p style={{
                            color: '#64748b',
                            fontSize: '13px',
                            margin: 0,
                            lineHeight: 1.55,
                        }}>
                            Access your courses, live sessions &amp; assessments
                        </p>
                    </div>

                    <div
                        className="choice-cta-pill"
                        style={{
                            background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '13px',
                            padding: '13px 32px',
                            borderRadius: '100px',
                            letterSpacing: '0.5px',
                            boxShadow: '0 4px 16px rgba(59,130,246,0.35)',
                            width: '100%',
                            textAlign: 'center',
                            boxSizing: 'border-box',
                        }}
                    >
                        Student Dashboard →
                    </div>
                </button>

                {/* ── Job Portal Card ── */}
                <button
                    className="choice-card-btn"
                    onClick={onChooseJobPortal}
                    style={{
                        background: '#fffbeb',
                        border: '1.5px solid #fde68a',
                        borderLeft: '5px solid #d4af37',
                        borderRadius: '18px',
                        padding: '32px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '18px',
                        textAlign: 'center',
                    }}
                >
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #92400e 0%, #d4af37 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '26px',
                        boxShadow: '0 8px 24px rgba(212,175,55,0.3)',
                        color: '#fff',
                        flexShrink: 0,
                    }}><FaBriefcase /></div>

                    <div style={{ flex: 1, width: '100%' }}>
                        <h3 style={{
                            color: '#78350f',
                            fontSize: '17px',
                            fontWeight: 800,
                            margin: '0 0 7px',
                        }}>Job Portal</h3>
                        <p style={{
                            color: '#92400e',
                            fontSize: '13px',
                            margin: 0,
                            lineHeight: 1.55,
                            opacity: 0.75,
                        }}>
                            Explore career opportunities that match your skills
                        </p>
                    </div>

                    <div
                        className="choice-cta-pill"
                        style={{
                            background: 'linear-gradient(135deg, #92400e, #d4af37)',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '13px',
                            padding: '13px 32px',
                            borderRadius: '100px',
                            letterSpacing: '0.5px',
                            boxShadow: '0 4px 16px rgba(212,175,55,0.3)',
                            width: '100%',
                            textAlign: 'center',
                            boxSizing: 'border-box',
                        }}
                    >
                        Job Dashboard →
                    </div>
                </button>
            </div>

            {/* Footer note */}
            <p style={{
                color: '#94a3b8',
                fontSize: '12px',
                marginTop: '28px',
                letterSpacing: '0.3px',
            }}>
                You can switch dashboards at any time from your profile menu.
            </p>
        </div>
    </div>
);

// ─── Premium Signup Verification Modal ──────────────────────────────────────────
const SignupVerificationModal = ({ email, otp, setOtp, onVerify, onResend, onEditEmail, resendTimer, loading, error, successMessage }) => (
    <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(240, 245, 255, 0.95)',
        backdropFilter: 'blur(30px)',
        WebkitBackdropFilter: 'blur(30px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
    }}>
        <style>{`
            @keyframes modalFadeIn {
                from { opacity: 0; transform: translateY(30px) scale(0.95); }
                to   { opacity: 1; transform: translateY(0)    scale(1);    }
            }
            .otp-input-field {
                width: 100%;
                text-align: center;
                font-size: 32px;
                letter-spacing: 12px;
                font-weight: 800;
                color: #002C5F;
                background: #f8fafc;
                border: 2px solid #e2e8f0;
                border-radius: 16px;
                padding: 15px;
                transition: all 0.3s ease;
            }
            .otp-input-field:focus {
                outline: none;
                border-color: #D4AF37;
                background: white;
                box-shadow: 0 0 0 5px rgba(212, 175, 55, 0.15);
            }
        `}</style>

        <div style={{
            background: 'white',
            borderRadius: '40px',
            padding: '60px',
            maxWidth: '520px',
            width: '100%',
            boxShadow: '0 40px 100px rgba(0, 44, 95, 0.15), 0 0 0 1px rgba(0,0,0,0.05)',
            textAlign: 'center',
            animation: 'modalFadeIn 0.6s cubic-bezier(0.16,1,0.3,1) both',
            position: 'relative',
        }}>
            <div style={{
                fontSize: '48px',
                marginBottom: '24px',
                filter: 'drop-shadow(0 4px 12px rgba(212,175,55,0.3))'
            }}>✉️</div>

            <h2 style={{
                fontSize: '28px',
                fontWeight: 900,
                color: '#002C5F',
                margin: '0 0 12px',
                letterSpacing: '-0.5px'
            }}>Verification Code</h2>

            <p style={{
                color: '#64748b',
                fontSize: '15px',
                lineHeight: 1.6,
                margin: '0 0 35px'
            }}>
                We've sent a 4-digit code to <br />
                <strong style={{ color: '#002C5F', fontWeight: 700 }}>{email}</strong>
            </p>

            <form onSubmit={(e) => { e.preventDefault(); onVerify(); }} style={{ marginBottom: '30px' }}>
                <div style={{ marginBottom: '25px' }}>
                    <input
                        className="otp-input-field"
                        type="text"
                        placeholder="0000"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        required
                        autoFocus
                        maxLength={4}
                    />
                </div>

                {error && <div style={{ color: '#ef4444', fontSize: '14px', marginBottom: '20px', fontWeight: 600 }}>{error}</div>}
                {successMessage && <div style={{ color: '#10b981', fontSize: '14px', marginBottom: '20px', fontWeight: 600 }}>{successMessage}</div>}

                <button
                    type="submit"
                    disabled={otp.length !== 4 || loading}
                    className="submit-btn primary"
                    style={{
                        padding: '18px',
                        fontSize: '16px',
                        background: otp.length === 4 ? '#002C5F' : '#cbd5e1',
                        boxShadow: otp.length === 4 ? '0 10px 25px rgba(0, 44, 95, 0.2)' : 'none'
                    }}
                >
                    {loading ? 'Verifying...' : 'Verify & Complete ✦'}
                </button>
            </form>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                <button
                    type="button"
                    onClick={onEditEmail}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#64748b',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: '8px'
                    }}
                >
                    ← Change Email
                </button>

                <button
                    type="button"
                    onClick={onResend}
                    disabled={resendTimer > 0 || loading}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: resendTimer > 0 ? '#94a3b8' : '#D4AF37',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: resendTimer > 0 ? 'default' : 'pointer',
                        padding: '8px'
                    }}
                >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                </button>
            </div>
        </div>
    </div>
);

// ─── Main AuthPage Component ────────────────────────────────────────────────────
const AuthPage = () => {
    const [view, setView] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [role, setRole] = useState('student');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showChoiceModal, setShowChoiceModal] = useState(false);
    const [loggedInUserName, setLoggedInUserName] = useState('');
    const [signupStep, setSignupStep] = useState('details'); // 'details' or 'otp'
    const [otp, setOtp] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const [showOTPModal, setShowOTPModal] = useState(false);

    const { user, login } = useContext(AuthContext);
    const navigate = useNavigate();

    // Auto-redirect if already logged in
    React.useEffect(() => {
        if (user) {
            const { role: userRole, hasEnrollments } = user;
            if (['super_admin', 'course_admin', 'finance_admin', 'reports_staff', 'tutor'].includes(userRole)) {
                navigate('/admin');
            } else if (userRole === 'recruiter') {
                navigate('/recruiter');
            } else if (userRole === 'job_seeker') {
                navigate('/jobseeker-dashboard');
            } else if (userRole === 'student') {
                if (hasEnrollments) {
                    // Show choice modal or just go to student dash? 
                    // Usually if they nav to /auth they just want to be "back" in their dash.
                    navigate('/student-dashboard');
                } else {
                    navigate('/student-dashboard');
                }
            }
        }
    }, [user, navigate]);

    // Timer effect for resend OTP
    React.useEffect(() => {
        let timer;
        if (resendTimer > 0) {
            timer = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendTimer]);

    const showJobPortalComingSoon = () => {
        setShowChoiceModal(false);
        import('sweetalert2').then(({ default: Swal }) => {
            Swal.fire({
                icon: 'info',
                title: '🚧 Job Portal — Coming Soon!',
                html: '<p style="color:#555;font-size:15px;line-height:1.6">The Job Portal is currently under active development.<br/>We\'re building something amazing for your career — check back soon!</p>',
                confirmButtonText: 'Go to Student Dashboard',
                confirmButtonColor: '#1e40af',
                customClass: { popup: 'swal-wide' },
            }).then(() => navigate('/student-dashboard'));
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setLoading(true);

        try {
            if (view === 'forgot') {
                const res = await axios.post('/api/auth/forgot-password', { email });
                setSuccessMessage(res.data.message);
                setTimeout(() => setView('login'), 3000);

            } else if (view === 'signup') {
                const res = await axios.post('/api/auth/send-otp', { email, name });
                setSuccessMessage(res.data.message);
                setShowOTPModal(true);
                setResendTimer(60);
                return; // Modal will handle the registration
            } else {
                // ─── LOGIN ───
                const res = await axios.post('/api/auth/login', { email, password });
                login(res.data.user, res.data.token);

                const { role: userRole, name: userName, hasEnrollments } = res.data.user;

                if (['super_admin', 'course_admin', 'finance_admin', 'reports_staff', 'tutor'].includes(userRole)) {
                    navigate('/admin');

                } else if (userRole === 'recruiter') {
                    navigate('/recruiter');

                } else if (userRole === 'job_seeker') {
                    navigate('/jobseeker-dashboard');

                } else if (userRole === 'student') {
                    if (hasEnrollments) {
                        // Show the premium choice modal
                        setLoggedInUserName(userName ? userName.split(' ')[0] : 'there');
                        setShowChoiceModal(true);
                    } else {
                        // No enrollments → go directly to student dashboard
                        navigate('/student-dashboard');
                    }

                } else {
                    import('sweetalert2').then(({ default: Swal }) => {
                        Swal.fire({
                            icon: 'success',
                            title: 'Login Successful',
                            text: 'Your dashboard is currently under development.',
                            confirmButtonColor: '#002C5F',
                        });
                    });
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Authentication failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (resendTimer > 0) return;
        setError('');
        setSuccessMessage('');
        setLoading(true);
        try {
            const res = await axios.post('/api/auth/send-otp', { email, name });
            setSuccessMessage('A new verification code has been sent.');
            setResendTimer(60);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resend OTP.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {showChoiceModal && (
                <DashboardChoiceModal
                    userName={loggedInUserName}
                    onChooseLearning={() => {
                        setShowChoiceModal(false);
                        navigate('/student-dashboard');
                    }}
                    onChooseJobPortal={() => {
                        setShowChoiceModal(false);
                        navigate('/jobseeker-dashboard');
                    }}
                />
            )}

            {showOTPModal && (
                <SignupVerificationModal
                    email={email}
                    otp={otp}
                    setOtp={setOtp}
                    resendTimer={resendTimer}
                    loading={loading}
                    error={error}
                    successMessage={successMessage}
                    onVerify={async () => {
                        setError('');
                        setSuccessMessage('');
                        setLoading(true);
                        try {
                            const res = await axios.post('/api/auth/register', { name, email, password, role, otp });
                            login(res.data.user, res.data.token);
                            setShowOTPModal(false);
                            if (role === 'student') {
                                navigate('/student-dashboard');
                            } else if (role === 'job_seeker') {
                                navigate('/jobseeker-dashboard');
                            } else {
                                navigate('/student-dashboard');
                            }
                        } catch (err) {
                            setError(err.response?.data?.message || 'Verification failed.');
                        } finally {
                            setLoading(false);
                        }
                    }}
                    onResend={handleResendOTP}
                    onEditEmail={() => {
                        setShowOTPModal(false);
                        setOtp('');
                        setError('');
                        setSuccessMessage('');
                    }}
                />
            )}

            <div className="auth-container">
                {loading && <Loader fullScreen={true} />}
                <div className="auth-left">
                    <div className="auth-left-content">
                        <div className="logo-container">
                            <img src="/logo.png" alt="Company Logo" className="brand-logo" />
                        </div>
                        <div className="theme-graphic">
                            <h1>Welcome to the Premium Portal</h1>
                            <p>Elevate your journey with our state-of-the-art platform.</p>
                            <div className="glass-card">
                                <div className="glass-icon">★</div>
                                <div>
                                    <h4>Premium Access</h4>
                                    <span>Unlock exclusive opportunities globally.</span>
                                </div>
                            </div>
                            <div className="glass-card">
                                <div className="glass-icon">🔒</div>
                                <div>
                                    <h4>Highly Secure</h4>
                                    <span>Advanced encryption for your peace of mind.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="auth-right">
                    <div className="auth-form-container">

                        <h2>
                            {view === 'login' ? 'Secure Sign In' : view === 'signup' ? 'Create an Account' : 'Reset Password'}
                        </h2>
                        <p className="subtitle">
                            {view === 'login' && 'Access your dashboard.'}
                            {view === 'signup' && 'Join us to start your professional journey.'}
                            {view === 'forgot' && 'Enter your email to receive a temporary password.'}
                        </p>

                        <form onSubmit={handleSubmit}>
                            {view === 'signup' && (
                                <>
                                    <div className="input-group">
                                        <label>Register As</label>
                                        <select
                                            value={role}
                                            onChange={(e) => setRole(e.target.value)}
                                            required
                                            className="role-select"
                                            style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)', background: 'white' }}
                                        >
                                            <option value="student">Student</option>
                                            <option value="job_seeker">Job Seeker</option>
                                        </select>
                                    </div>
                                    <div className="input-group">
                                        <label>Full Name</label>
                                        <input
                                            type="text"
                                            placeholder="Enter your name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                        />
                                    </div>
                                </>
                            )}

                            <div className="input-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    placeholder="name@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                    required
                                />
                            </div>

                            {view !== 'forgot' && (
                                <div className="input-group">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    {view === 'login' && (
                                        <div className="forgot-password-link">
                                            <button type="button" onClick={() => { setView('forgot'); setError(''); setSuccessMessage(''); }}>
                                                Forgot Password?
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {error && <div className="error-message">{error}</div>}
                            {successMessage && <div className="success-message">{successMessage}</div>}

                            <button type="submit" className="submit-btn primary">
                                {view === 'login' ? 'Sign In' : (view === 'signup' ? 'Get Verification Code ✦' : 'Send Reset Link')}
                            </button>
                        </form>

                        {view !== 'forgot' && (
                            <div className="form-footer">
                                <p>
                                    {view === 'login' ? "Don't have an account?" : "Already have an account?"}
                                    <button
                                        type="button"
                                        className="link-btn"
                                        onClick={() => {
                                            setView(view === 'login' ? 'signup' : 'login');
                                            setError('');
                                            setSuccessMessage('');
                                        }}
                                    >
                                        {view === 'login' ? 'Sign Up' : 'Log In'}
                                    </button>
                                </p>
                            </div>
                        )}

                        {view === 'forgot' && (
                            <div className="form-footer">
                                <p>
                                    Remembered your password?
                                    <button type="button" className="link-btn" onClick={() => setView('login')}>
                                        Back to Login
                                    </button>
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AuthPage;
